// 使用方式
// import VideoChunkUploader from "@/utils/videoChunkUploader";

// const uploader = new VideoChunkUploader({
//   file,
//   chunkSize: 1024 * 1024,
//   uploadUrl: "/api/upload/video_chunk",
//   onProgress: (progress) => console.log(progress),
//   onSuccess: (res) => console.log("完成", res),
//   onError: (err) => console.error(err),
// });

// uploader.start();

import SparkMD5 from "spark-md5";
import request from "../api/request";

export interface VideoChunkUploaderOptions {
  /**
   * 待上传的原始文件
   */
  file: File;
  /**
   * 每个切片的大小（字节），默认 1MB
   */
  chunkSize?: number;
  /**
   * 上传接口地址，默认 /api/upload/video_chunk
   */
  uploadUrl?: string;
  /**
   * 自定义 upload_id（可用于断点续传），默认使用 UUID
   */
  uploadId?: string;
  /**
   * 附加到 FormData 的额外字段
   */
  extraData?: Record<string, string | Blob>;
  /**
   * 上传进度回调（0-100）
   */
  onProgress?: (progress: number) => void;
  /**
   * 上传成功回调（最后一个切片完成后触发）
   */
  onSuccess?: (result: any) => void;
  /**
   * 上传失败回调
   */
  onError?: (error: Error) => void;
  /**
   * 文件 hash 计算进度回调（0-100）
   */
  onHashProgress?: (progress: number) => void;
}

interface ChunkInfo {
  index: number;
  blob: Blob;
  size: number;
}

/**
 * 基于 /api/upload/video_chunk 接口的大文件分片上传器
 *
 * 使用方式：
 * const uploader = new VideoChunkUploader({ file });
 * await uploader.start();
 */
export default class VideoChunkUploader {
  private readonly file: File;
  private readonly chunkSize: number;
  private readonly uploadUrl: string;
  private readonly uploadId: string;
  private readonly extraData: Record<string, string | Blob>;

  private readonly onProgress: (progress: number) => void;
  private readonly onSuccess: (result: any) => void;
  private readonly onError: (error: Error) => void;
  private readonly onHashProgress: (progress: number) => void;

  private fileHash = "";
  private chunks: ChunkInfo[] = [];
  private uploadedSize = 0;
  private lastResponse: any = null;
  private isUploading = false;
  private shouldAbort = false;

  constructor(options: VideoChunkUploaderOptions) {
    this.file = options.file;
    this.chunkSize = options.chunkSize || 1024 * 1024; // 1MB
    this.uploadUrl = options.uploadUrl || "/api/upload/video_chunk";
    this.uploadId = options.uploadId || this.generateUploadId();
    this.extraData = options.extraData || {};

    this.onProgress = options.onProgress || (() => {});
    this.onSuccess = options.onSuccess || (() => {});
    this.onError = options.onError || (() => {});
    this.onHashProgress = options.onHashProgress || (() => {});
  }

  /**
   * 开始上传流程
   */
  async start(): Promise<void> {
    if (this.isUploading) return;
    this.isUploading = true;
    this.shouldAbort = false;

    try {
      this.fileHash = await this.calculateFileHash();
      if (this.shouldAbort) return;

      this.chunks = this.createChunks();
      await this.uploadChunksSequentially();
      if (!this.shouldAbort) {
        this.onSuccess(this.lastResponse);
      }
    } catch (error) {
      if (error instanceof Error) {
        this.onError(error);
      } else {
        this.onError(new Error(String(error)));
      }
    } finally {
      this.isUploading = false;
    }
  }

  /**
   * 终止上传
   */
  abort(): void {
    this.shouldAbort = true;
    this.isUploading = false;
  }

  private async uploadChunksSequentially(): Promise<void> {
    this.uploadedSize = 0;
    const totalSize = this.file.size;

    for (const chunk of this.chunks) {
      if (this.shouldAbort) break;
      const response = await this.uploadSingleChunk(chunk);
      this.lastResponse = response;
      this.uploadedSize += chunk.size;
      const progress = Math.floor((this.uploadedSize / totalSize) * 100);
      this.onProgress(progress);
    }
  }

  private async uploadSingleChunk(chunk: ChunkInfo): Promise<any> {
    const formData = new FormData();
    formData.append("file", chunk.blob, this.file.name);
    formData.append("slice_index", String(chunk.index));
    formData.append("total_slices", String(this.chunks.length));
    formData.append("upload_id", this.uploadId);
    formData.append("original_filename", this.file.name);
    formData.append("file_hash", this.fileHash);

    Object.entries(this.extraData).forEach(([key, value]) => {
      formData.append(key, value);
    });

    try {
      return await request.post(this.uploadUrl, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(`切片 ${chunk.index} 上传失败: ${message}`);
    }
  }

  private createChunks(): ChunkInfo[] {
    const chunks: ChunkInfo[] = [];
    let start = 0;
    let index = 0;

    while (start < this.file.size) {
      const end = Math.min(start + this.chunkSize, this.file.size);
      const blob = this.file.slice(start, end);
      chunks.push({
        index,
        blob,
        size: blob.size,
      });
      start = end;
      index += 1;
    }

    return chunks;
  }

  private async calculateFileHash(): Promise<string> {
    return new Promise((resolve, reject) => {
      const spark = new SparkMD5.ArrayBuffer();
      const fileReader = new FileReader();
      const chunkSize = 2 * 1024 * 1024; // 2MB
      const totalChunks = Math.ceil(this.file.size / chunkSize);
      let currentChunk = 0;

      const loadNext = () => {
        if (this.shouldAbort) {
          reject(new Error("哈希计算已终止"));
          return;
        }
        const start = currentChunk * chunkSize;
        const end = Math.min(start + chunkSize, this.file.size);
        fileReader.readAsArrayBuffer(this.file.slice(start, end));
      };

      fileReader.onload = (e) => {
        if (this.shouldAbort) {
          reject(new Error("哈希计算已终止"));
          return;
        }

        const result = e.target?.result;
        if (result instanceof ArrayBuffer) {
          spark.append(result);
          currentChunk += 1;
          this.onHashProgress(
            Math.floor((currentChunk / totalChunks) * 100) || 0
          );

          if (currentChunk < totalChunks) {
            loadNext();
          } else {
            resolve(spark.end());
          }
        } else {
          reject(new Error("文件读取失败，无法计算哈希"));
        }
      };

      fileReader.onerror = () => {
        reject(new Error("哈希计算失败: 文件读取错误"));
      };

      loadNext();
    });
  }

  private generateUploadId(): string {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
}
